import { useRef, useState } from "react";
import { useCursor, Image, Html } from "@react-three/drei";
import { useLocation, useRoute } from "wouter";
import getUuid from "uuid-by-string";
import { useFrame } from "@react-three/fiber";
import { easing } from "maath";

interface ItemProp {
  title: string;
  text: string;
}

function Frame({
  url,
  item,
  ...props
}: {
  url: string;
  item: ItemProp;
  text: string;
}) {
  const image = useRef<any>(null!);
  const frame = useRef<any>(null!);
  const [, params] = useRoute("/item/:id");
  const [location, setLocation] = useLocation();

  const [hovered, hover] = useState(false);
  const [rnd] = useState(() => Math.random());
  const name = getUuid(url);
  const { title, text } = item;

  const isActive = params?.id === name;

  useCursor(hovered);
  useFrame((state, dt) => {
    image.current.material.zoom =
      0.9 - Math.sin(rnd * 10000 + state.clock.elapsedTime / 8) / 10.5;
    easing.damp3(
      image.current.scale,
      [
        0.85 * (!isActive && hovered ? 0.85 : 1),
        0.9 * (!isActive && hovered ? 0.905 : 1),
        1,
      ],
      0.1,
      dt
    );
    easing.dampC(
      frame.current.material.color,
      hovered ? "yellow" : "white",
      0.1,
      dt
    );
  });
  return (
    <group {...props}>
      <mesh
        name={name}
        onPointerOver={(e) => {
          e.stopPropagation();
          hover(true);
        }}
        onPointerOut={() => hover(false)}
        scale={[1.5, 1.8, 0.15]}
        position={[0, 0.95, 0]}
      >
        <boxGeometry />
        <meshStandardMaterial
          color="#151515"
          metalness={0.5}
          roughness={0.5}
          envMapIntensity={2}
        />
        <mesh
          ref={frame}
          raycast={() => null}
          scale={[0.9, 0.93, 0.9]}
          position={[0, 0, 0.2]}
        >
          <boxGeometry />
          <meshBasicMaterial toneMapped={false} fog={false} />
        </mesh>
        <Image
          raycast={() => null}
          ref={image}
          position={[0, 0, 0.7]}
          url={url}
        >
          <Html scale={0.1} occlude position={[0, 0, 0]} transform center>
            <div
              className="bg-transparent flex flex-col justify-between"
              onClick={(e) => {
                e.stopPropagation();
                setLocation(
                  location.split("/")[2] === name ? "/" : "/item/" + name
                );
              }}
            >
              <div className="w-72 flex flex-col justify-center rounded-lg p-2 bg-slate-950/70 cursor-pointer">
                <span
                  className="text-[#fff] text-base font-bold"
                  style={{ userSelect: "none" }}
                >
                  {title}
                </span>
                <span
                  className="bg-transparent text-xs font-semibold text-gray-200"
                  style={{ userSelect: "none" }}
                >
                  {text}
                </span>
              </div>
            </div>
          </Html>
        </Image>
      </mesh>
    </group>
  );
}

export default Frame;
